import { FC, MouseEvent, ReactNode } from 'react';
import { NavLink } from 'react-router-dom';

import { Box, rem, Tooltip } from '@bonusly/design-system';

import { useIsRecognize } from '../../../hooks/use-is-recognize';
import { useCloseNavbarPanels } from '../../../hooks/use-navbar-panels';
import { useLinkNavigation } from '../hooks/use-link-navigation';

const getStyle = (isActive: boolean) => {
	if (isActive) {
		return {
			bg: 'primary.bgAlpha',
			color: 'primary.fgStrong',
		};
	}

	return {
		bg: 'none',
		color: 'secondary.fg',
		_hover: { bg: 'secondary.bgAlpha' },
	};
};

// Check if we're running as an MFE embedded in Special Sauce
const isRunningAsMFE = () => {
	return typeof document !== 'undefined' && document.getElementById('user-sidebar-root') !== null;
};

// Check if the current path matches the link path (for MFE active state)
// Also considers whether the link targets the same domain we're currently on
const isPathActive = (linkPath: string, isRecognizeRoute: boolean, onRecognizePage: boolean): boolean => {
	// If the link targets a different domain than we're on, it can't be active
	if (isRecognizeRoute !== onRecognizePage) {
		return false;
	}

	const currentPath = window.location.pathname;
	// Exact match or starts with the path (for nested routes)
	return currentPath === linkPath || currentPath.startsWith(linkPath + '/');
};

type UserNavLinkProps = {
	children: ReactNode;
	isRecognizeRoute?: boolean;
	to: string;
	/** Label used for tooltip when sidebar is collapsed */
	label?: string;
	/** Controls tooltip visibility - tooltip shown when false */
	isExpanded?: boolean;
	/** Optional click handler called before navigation */
	onClick?: () => void;
};

export const UserNavLink: FC<UserNavLinkProps> = ({ children, to, isRecognizeRoute = false, label, isExpanded, onClick, ...props }) => {
	const { isCrossDomain, url } = useLinkNavigation({ path: to, isRecognizeRoute });
	const onRecognizePage = useIsRecognize();
	const isMFE = isRunningAsMFE();
	const closeAllPanels = useCloseNavbarPanels();

	const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
		// Call custom onClick handler if provided
		onClick?.();
		closeAllPanels();
		window.scrollTo({ top: 0 });
		if (isCrossDomain) {
			e.preventDefault();
			window.location.href = url;
		}
	};

	// When running as MFE, determine active state from window.location instead of React Router
	// Also checks that the link targets the same domain we're currently on
	const mfeIsActive = isMFE ? isPathActive(to, isRecognizeRoute, onRecognizePage) : false;

	// Use full URL for href when cross-domain to support middle-click/right-click "Open in new tab"
	const href = isCrossDomain ? url : to;

	const linkContent = (
		<Box
			as={NavLink}
			to={href}
			onClick={handleClick}
			w="100%"
			borderRadius="sm"
			_focusVisible={{ outline: '2px solid', outlineColor: 'border.focus', outlineOffset: '-2px' }}
			{...props}
		>
			{({ isActive: routerIsActive }: { isActive: boolean }) => {
				// Use MFE active state when in MFE mode, otherwise use React Router's active state
				const isActive = isMFE ? mfeIsActive : routerIsActive;
				return (
					<Box {...getStyle(isActive)} w="100%" py={rem(8)} px={rem(10)} borderRadius="md">
						{children}
					</Box>
				);
			}}
		</Box>
	);

	return (
		<Tooltip label={label} placement="right" isDisabled={isExpanded || !label}>
			{linkContent}
		</Tooltip>
	);
};
